import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientSession } from '@/lib/ClientSessionContext';
import { useNavigate } from 'react-router-dom';
import { 
  Database, Search, Sparkles, ShoppingBag, Eye, X, 
  FlaskConical, Package, CheckCircle2, ChevronRight, Layers,
  Tag, Info, Filter, ArrowUpDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const fmt = (n) => new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(n);

// Helper hitung jasa maklon per botol
const hitungJasa = (ml) => {
  if (!ml || ml === 0) return 0;
  if (ml <= 10) return 4000;
  if (ml <= 30) return 10000;
  if (ml <= 50) return 15000;
  return 20000;
};

// Helper harga jual master
const getHargaJual = (item) => item?.harga_jual || (item?.harga_rupiah ? item.harga_rupiah * 1.3 : 0);

export default function ClientDatabaseProduk() {
  const navigate = useNavigate();
  const { clientUser } = useClientSession();
  
  const isDeveloper = clientUser?.role === 'developer' || clientUser?.role === 'admin';
  const assignedBrands = isDeveloper ? [] : (clientUser?.assigned_brands || (clientUser?.brand_name ? [clientUser.brand_name] : []));

  // Queries
  const { data: racikanList = [], isLoading: loadingRacikan } = useQuery({
    queryKey: ['racikan-database'],
    queryFn: () => base44.entities.RacikanDatabase.list('nama_brand'),
  });

  const { data: bahanList = [], isLoading: loadingBahan } = useQuery({
    queryKey: ['database-bahan'],
    queryFn: () => base44.entities.DatabaseBahan.list('nama_brand'),
  });

  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: masterBahanCair = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrandFilter, setSelectedBrandFilter] = useState('ALL');
  const [selectedVariantDetail, setSelectedVariantDetail] = useState(null);
  const [activeSizeTab, setActiveSizeTab] = useState(0);

  // Kumpulan brand yang tersedia sesuai hak akses
  const allDbBrands = useMemo(() => {
    const set = new Set();
    racikanList.forEach(r => { if (r.nama_brand) set.add(r.nama_brand.trim()); });
    bahanList.forEach(b => { if (b.nama_brand) set.add(b.nama_brand.trim()); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [racikanList, bahanList]);

  const accessibleBrands = useMemo(() => {
    if (isDeveloper) return allDbBrands;
    if (assignedBrands.length > 0) return assignedBrands;
    return allDbBrands;
  }, [isDeveloper, assignedBrands, allDbBrands]);

  // Set default brand filter jika hanya punya 1 brand
  useMemo(() => {
    if (accessibleBrands.length === 1 && selectedBrandFilter === 'ALL') {
      setSelectedBrandFilter(accessibleBrands[0]);
    }
  }, [accessibleBrands, selectedBrandFilter]);

  // Helper kalkulasi harga jual akhir produk (HANYA TOTAL JUAL, TANPA HARGA SATUAN BAHAN)
  const calculateFinalSellPrice = (sizeObj) => {
    const b = sizeObj?.bahan;
    const r = sizeObj?.racikan;
    const ml = parseFloat(sizeObj?.size) || b?.botol_ukuran_label_ml || r?.ukuran_ml || 30;

    // Cek custom harga jual dari database
    if (b?.custom_harga_jual !== undefined && b?.custom_harga_jual !== null && b?.custom_harga_jual !== '') {
      return Number(b.custom_harga_jual);
    }

    const matchedBotol = b?.botol_nama ? botolList.find(x => (x.nama || '').trim().toLowerCase() === (b.botol_nama || '').trim().toLowerCase()) : null;
    const matchedTutup = b?.tutup_nama ? tutupList.find(x => (x.nama || '').trim().toLowerCase() === (b.tutup_nama || '').trim().toLowerCase()) : null;
    const matchedSpray = b?.spray_nama ? sprayList.find(x => (x.nama || '').trim().toLowerCase() === (b.spray_nama || '').trim().toLowerCase()) : null;

    const jualBotol = matchedBotol ? getHargaJual(matchedBotol) : 0;
    const jualTutup = matchedTutup ? getHargaJual(matchedTutup) : 0;
    const jualSpray = matchedSpray ? getHargaJual(matchedSpray) : 0;
    const jualPackaging = jualBotol + jualTutup + jualSpray;

    // Jual cairan racikan
    let jualRacikan = 0;
    const formulaItems = r?.racikan || [];
    formulaItems.forEach(item => {
      const persentase = parseFloat(String(item.persentase).replace(',', '.')) || 0;
      if (!persentase) return;
      const vol = (persentase / 100) * ml;
      const matchedIng = masterBahanCair.find(x => (x.nama || '').trim().toLowerCase() === (item.nama_bahan || '').trim().toLowerCase());
      const hargaJualPerSatuan = matchedIng ? (matchedIng.harga_jual || (matchedIng.harga_rupiah * 1.3) || 0) : 0;
      const perMl = hargaJualPerSatuan / 1000;
      jualRacikan += (vol * perMl);
    });

    // Jual Komponen tambahan
    const jualKomponen = (b?.item_tambahan || []).reduce((sum, k) => sum + (k.harga_jual || 0), 0);
    const jasaNominal = b?.custom_jasa !== undefined && b?.custom_jasa !== null ? Number(b.custom_jasa) : hitungJasa(ml);

    let total = Math.round(jualPackaging + jualRacikan + jasaNominal + jualKomponen);
    if (b?.custom_margin) {
      total += Number(b.custom_margin);
    }
    return total > 0 ? total : 35000; // fallback reasonable price
  };

  // Grouping Products & Variants
  const groupedProducts = useMemo(() => {
    const map = {};

    // Filter brand list based on accessible brands
    const targetBrands = selectedBrandFilter === 'ALL' ? accessibleBrands : [selectedBrandFilter];

    racikanList.forEach(r => {
      if (!r.nama_brand) return;
      const brandClean = r.nama_brand.trim();
      const isAllowed = targetBrands.some(tb => tb.toLowerCase() === brandClean.toLowerCase());
      if (!isAllowed) return;

      let varianOnly = r.nama_varian || r.nama_variant || '';
      let sName = r.ukuran_ml ? String(r.ukuran_ml) : '30';
      let joinKey = sName;

      if (varianOnly.includes(' || ')) {
        const parts = varianOnly.split(' || ');
        varianOnly = parts[0];
        sName = `${parts[1]} (${sName}ml)`;
        joinKey = parts[1];
      }

      const uniqueKey = `${brandClean}___${varianOnly}`;
      if (!map[uniqueKey]) {
        map[uniqueKey] = {
          id: uniqueKey,
          brand_name: brandClean,
          nama_varian: varianOnly,
          sizes: {}
        };
      }

      if (!map[uniqueKey].sizes[joinKey]) {
        map[uniqueKey].sizes[joinKey] = { size: sName, racikan: null, bahan: null };
      }
      map[uniqueKey].sizes[joinKey].racikan = r;
    });

    // Merge with DatabaseBahan
    bahanList.forEach(b => {
      if (!b.nama_brand) return;
      const brandClean = b.nama_brand.trim();
      const isAllowed = targetBrands.some(tb => tb.toLowerCase() === brandClean.toLowerCase());
      if (!isAllowed) return;

      let varianOnly = b.nama_varian || b.nama_variant || '';
      let sName = b.botol_ukuran_label_ml ? String(b.botol_ukuran_label_ml) : '30';
      let joinKey = sName;

      if (varianOnly.includes(' || ')) {
        const parts = varianOnly.split(' || ');
        varianOnly = parts[0];
        sName = `${parts[1]} (${sName}ml)`;
        joinKey = parts[1];
      }

      const uniqueKey = `${brandClean}___${varianOnly}`;
      if (!map[uniqueKey]) {
        map[uniqueKey] = {
          id: uniqueKey,
          brand_name: brandClean,
          nama_varian: varianOnly,
          sizes: {}
        };
      }

      if (!map[uniqueKey].sizes[joinKey]) {
        map[uniqueKey].sizes[joinKey] = { size: sName, racikan: null, bahan: null };
      }
      map[uniqueKey].sizes[joinKey].bahan = b;
    });

    return Object.values(map).map(prod => {
      const sortedSizes = Object.values(prod.sizes).sort(
        (a, b) => (parseFloat(a.size) || 0) - (parseFloat(b.size) || 0)
      );

      // Hitung harga untuk setiap ukuran
      const sizesWithPrice = sortedSizes.map(s => ({
        ...s,
        finalPrice: calculateFinalSellPrice(s)
      }));

      return {
        ...prod,
        sizes: sizesWithPrice,
        minPrice: sizesWithPrice.length > 0 ? Math.min(...sizesWithPrice.map(s => s.finalPrice)) : 0,
        maxPrice: sizesWithPrice.length > 0 ? Math.max(...sizesWithPrice.map(s => s.finalPrice)) : 0,
      };
    });
  }, [racikanList, bahanList, accessibleBrands, selectedBrandFilter, botolList, tutupList, sprayList, masterBahanCair, itemTambahanList]);

  // Filtered by Search Term
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return groupedProducts;
    const term = searchTerm.toLowerCase();
    return groupedProducts.filter(p => 
      p.nama_varian.toLowerCase().includes(term) ||
      p.brand_name.toLowerCase().includes(term)
    );
  }, [groupedProducts, searchTerm]);

  // Statistik Total
  const totalVariants = groupedProducts.length;
  const totalSizes = groupedProducts.reduce((sum, p) => sum + p.sizes.length, 0);
  const totalUniqueBrands = useMemo(() => {
    return new Set(groupedProducts.map(p => p.brand_name)).size;
  }, [groupedProducts]);

  const isLoading = loadingRacikan || loadingBahan;

  return (
    <div className="max-w-6xl mx-auto pb-24 animate-in fade-in duration-300">
      
      {/* Header Section */}
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between border-b border-[#E6D5B8] pb-6 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-[#8C6208]/10 text-[#8C6208] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider border border-[#8C6208]/20">
              Katalog Formula & Produk
            </span>
          </div>
          <h1 className="text-3xl font-black text-[#302621] tracking-tight">Database Produk Brand</h1>
          <p className="text-sm text-[#7A695B] mt-1">
            Katalog resmi formula racikan, spesifikasi botol & kemasan, serta harga jual akhir per varian untuk brand Anda.
          </p>
        </div>

        <Button
          onClick={() => navigate('/pesan-po')}
          className="bg-[#302621] hover:bg-[#1A1411] text-white font-bold text-xs h-11 px-5 rounded-2xl shadow-md gap-2 shrink-0"
        >
          <ShoppingBag className="w-4 h-4" />
          <span>Buat Pesanan (PO)</span>
        </Button>
      </div>

      {/* Metric Cards (Tanpa Keterangan Integrasi) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white p-5 rounded-2xl border border-[#E6D5B8] shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#F9F5EC] border border-[#E6D5B8] flex items-center justify-center text-[#8C6208]">
            <Database className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[11px] font-bold text-[#7A695B] uppercase tracking-wider">Total Brand Mitra</p>
            <h3 className="text-2xl font-black text-[#302621]">{totalUniqueBrands} Brand</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#E6D5B8] shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#F9F5EC] border border-[#E6D5B8] flex items-center justify-center text-[#8C6208]">
            <FlaskConical className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[11px] font-bold text-[#7A695B] uppercase tracking-wider">Total Varian Aroma</p>
            <h3 className="text-2xl font-black text-[#302621]">{totalVariants} Varian</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#E6D5B8] shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#F9F5EC] border border-[#E6D5B8] flex items-center justify-center text-[#8C6208]">
            <Package className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[11px] font-bold text-[#7A695B] uppercase tracking-wider">Opsi Kemasan / Size</p>
            <h3 className="text-2xl font-black text-[#302621]">{totalSizes} Ukuran</h3>
          </div>
        </div>
      </div>

      {/* Controls: Brand Filter Tabs & Search */}
      <div className="bg-white p-4 rounded-2xl border border-[#E6D5B8] shadow-sm mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
        
        {/* Brand Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          {accessibleBrands.length > 1 && (
            <button
              onClick={() => setSelectedBrandFilter('ALL')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shrink-0 ${
                selectedBrandFilter === 'ALL'
                  ? 'bg-[#8C6208] text-white shadow-sm'
                  : 'bg-[#F9F5EC] text-[#7A695B] hover:text-[#302621]'
              }`}
            >
              Semua Brand ({accessibleBrands.length})
            </button>
          )}

          {accessibleBrands.map(b => (
            <button
              key={b}
              onClick={() => setSelectedBrandFilter(b)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all shrink-0 ${
                selectedBrandFilter === b
                  ? 'bg-[#8C6208] text-white shadow-sm'
                  : 'bg-[#F9F5EC] text-[#7A695B] hover:text-[#302621]'
              }`}
            >
              {b}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
          <Input 
            placeholder="Cari nama varian..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10 rounded-xl border-[#E6D5B8] focus-visible:ring-[#8C6208] text-xs bg-[#FFFDF9]"
          />
        </div>

      </div>

      {/* Grid of Products */}
      {isLoading ? (
        <div className="py-20 text-center text-[#7A695B] text-sm">
          <FlaskConical className="w-8 h-8 text-[#8C6208] animate-bounce mx-auto mb-3" />
          Memuat database produk brand...
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white p-12 rounded-3xl border-2 border-dashed border-[#E6D5B8] text-center max-w-lg mx-auto">
          <Package className="w-12 h-12 text-[#A39171] mx-auto mb-3 opacity-60" />
          <h3 className="text-lg font-bold text-[#302621]">Belum Ada Produk Ditemukan</h3>
          <p className="text-xs text-[#7A695B] mt-1 mb-4">
            Tidak ada varian formula yang terdaftar untuk filter atau pencarian ini.
          </p>
          {searchTerm && (
            <Button 
              variant="outline" 
              onClick={() => setSearchTerm('')} 
              className="text-xs font-bold rounded-xl border-[#E6D5B8]"
            >
              Reset Pencarian
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((prod) => {
            const defaultSize = prod.sizes[0];
            const hasMultipleSizes = prod.sizes.length > 1;

            return (
              <div 
                key={prod.id} 
                className="bg-white border border-[#E6D5B8] rounded-3xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group relative overflow-hidden"
              >
                {/* Brand Badge */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#8C6208] bg-[#F9F5EC] border border-[#E6D5B8] px-2.5 py-1 rounded-lg">
                    {prod.brand_name}
                  </span>
                  <span className="text-[11px] font-semibold text-[#7A695B]">
                    {prod.sizes.length} Kemasan
                  </span>
                </div>

                {/* Varian Title */}
                <div className="mb-4">
                  <h3 className="text-xl font-black text-[#302621] group-hover:text-[#8C6208] transition-colors line-clamp-1">
                    {prod.nama_varian}
                  </h3>
                  <p className="text-xs text-[#7A695B] mt-0.5">
                    Eau de Parfum (EDP) • Formulasi Khusus
                  </p>
                </div>

                {/* Kemasan & Item Types */}
                <div className="bg-[#FFFDF9] border border-[#E6D5B8]/60 rounded-2xl p-4 mb-4 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-[#7A695B]">Pilihan Ukuran:</span>
                    <div className="flex gap-1.5 flex-wrap justify-end">
                      {prod.sizes.map((s, sIdx) => (
                        <span 
                          key={sIdx} 
                          className="bg-white border border-[#E6D5B8] text-[#302621] font-bold text-[10px] px-2 py-0.5 rounded-md"
                        >
                          {s.size} ml
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-[#7A695B]">Spesifikasi Kemasan:</span>
                    <span className="font-semibold text-[#302621] truncate max-w-[150px] text-right">
                      {defaultSize?.bahan?.botol_nama || 'Botol Standar'}
                    </span>
                  </div>

                  {defaultSize?.racikan?.racikan?.length > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#7A695B]">Komposisi Racikan:</span>
                      <span className="font-bold text-[#8C6208]">
                        {defaultSize.racikan.racikan.length} Komponen Aroma
                      </span>
                    </div>
                  )}
                </div>

                {/* Total Final Price (Hanya Harga Total Jual) */}
                <div className="border-t border-[#E6D5B8]/60 pt-4 mb-5 flex items-baseline justify-between">
                  <div>
                    <span className="text-[10px] font-bold text-[#7A695B] uppercase tracking-wider block">
                      Harga Jual Akhir
                    </span>
                    <p className="text-[10px] text-[#A39171]">Siap jual per pcs</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-black text-[#8C6208]">
                      {hasMultipleSizes ? (
                        <>Rp {fmt(prod.minPrice)} <span className="text-xs font-normal text-[#7A695B]">- {fmt(prod.maxPrice)}</span></>
                      ) : (
                        <>Rp {fmt(prod.minPrice)}</>
                      )}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedVariantDetail(prod);
                      setActiveSizeTab(0);
                    }}
                    className="border-[#E6D5B8] text-[#302621] hover:bg-[#F9F5EC] text-xs font-bold h-9 rounded-xl gap-1.5 shadow-2xs"
                  >
                    <Eye className="w-3.5 h-3.5 text-[#8C6208]" />
                    <span>Detail</span>
                  </Button>

                  <Button
                    size="sm"
                    onClick={() => navigate(`/pesan-po`)}
                    className="bg-[#302621] hover:bg-[#1A1411] text-white text-xs font-bold h-9 rounded-xl gap-1.5 shadow-md"
                  >
                    <ShoppingBag className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span>Order PO</span>
                  </Button>
                </div>

              </div>
            );
          })}
        </div>
      )}

      {/* DETAIL MODAL (Komposisi Racikan & Kemasan Lengkap Tanpa Harga Satuan Modal) */}
      {selectedVariantDetail && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-[#E6D5B8] flex flex-col max-h-[90vh] overflow-hidden">
            
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-[#E6D5B8] bg-gradient-to-r from-[#FFFDF9] to-[#F9F5EC] flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#8C6208] bg-white border border-[#E6D5B8] px-2.5 py-0.5 rounded-full">
                  Brand: {selectedVariantDetail.brand_name}
                </span>
                <h2 className="text-2xl font-black text-[#302621] mt-1">
                  {selectedVariantDetail.nama_varian}
                </h2>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setSelectedVariantDetail(null)} 
                className="rounded-full w-9 h-9 text-[#7A695B] hover:text-[#302621] hover:bg-[#E6D5B8]/40"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Size Selector Tabs */}
            {selectedVariantDetail.sizes.length > 1 && (
              <div className="flex border-b border-[#E6D5B8] bg-[#F9F5EC]/50 px-6 pt-3 gap-2">
                {selectedVariantDetail.sizes.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveSizeTab(idx)}
                    className={`pb-3 px-4 text-xs font-bold transition-all border-b-2 ${
                      activeSizeTab === idx
                        ? 'border-[#8C6208] text-[#8C6208]'
                        : 'border-transparent text-[#7A695B] hover:text-[#302621]'
                    }`}
                  >
                    Ukuran {s.size} ml
                  </button>
                ))}
              </div>
            )}

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {(() => {
                const curSize = selectedVariantDetail.sizes[activeSizeTab] || selectedVariantDetail.sizes[0];
                const b = curSize?.bahan;
                const r = curSize?.racikan;
                const racikanItems = r?.racikan || [];

                return (
                  <>
                    {/* Spesifikasi Kemasan */}
                    <div className="bg-[#FFFDF9] p-5 rounded-2xl border border-[#E6D5B8] space-y-3">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-[#8C6208] flex items-center gap-1.5">
                        <Package className="w-4 h-4" />
                        Spesifikasi Material Kemasan ({curSize?.size} ml)
                      </h4>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div className="bg-white p-3 rounded-xl border border-[#E6D5B8]/60">
                          <span className="text-[#7A695B] block text-[11px]">Jenis Botol:</span>
                          <span className="font-bold text-[#302621]">{b?.botol_nama || 'Botol Standar'} ({curSize?.size} ml)</span>
                        </div>

                        <div className="bg-white p-3 rounded-xl border border-[#E6D5B8]/60">
                          <span className="text-[#7A695B] block text-[11px]">Tutup & Spray:</span>
                          <span className="font-bold text-[#302621]">
                            {b?.tutup_nama ? `${b.tutup_nama} / ` : ''}{b?.spray_nama || 'Spray Standar'}
                          </span>
                        </div>
                      </div>

                      {/* Item Tambahan jika ada */}
                      {b?.item_tambahan && b.item_tambahan.length > 0 && (
                        <div className="pt-2">
                          <span className="text-[11px] font-bold text-[#7A695B] block mb-1.5">Item Tambahan:</span>
                          <div className="flex flex-wrap gap-1.5">
                            {b.item_tambahan.map((it, itIdx) => (
                              <Badge key={itIdx} variant="outline" className="text-xs border-[#E6D5B8] bg-white font-semibold">
                                {it.nama} ({it.kategori || 'Kemasan'})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Komposisi Racikan Formula Cairan (Tanpa Harga Modal/Bahan!) */}
                    <div className="border border-[#E6D5B8] rounded-2xl overflow-hidden bg-white shadow-sm">
                      <div className="px-5 py-3.5 bg-[#F9F5EC] border-b border-[#E6D5B8] flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-[#302621] flex items-center gap-1.5">
                          <FlaskConical className="w-4 h-4 text-[#8C6208]" />
                          Komposisi Formula Racikan
                        </span>
                        <span className="text-[11px] font-semibold text-[#8C6208]">
                          {racikanItems.length} Bahan Terdaftar
                        </span>
                      </div>

                      {racikanItems.length === 0 ? (
                        <div className="p-6 text-center text-xs text-slate-400 italic">
                          Komposisi cairan racikan sedang disinkronisasikan oleh tim laboratorium.
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100 text-xs">
                          {racikanItems.map((rc, rcIdx) => {
                            const pct = parseFloat(String(rc.persentase).replace(',', '.')) || 0;
                            return (
                              <div key={rcIdx} className="p-3.5 flex items-center justify-between hover:bg-[#FFFDF9]">
                                <div className="flex items-center gap-3">
                                  <span className="w-6 h-6 rounded-full bg-[#F9F5EC] text-[#8C6208] flex items-center justify-center font-bold text-[10px] border border-[#E6D5B8]">
                                    {rcIdx + 1}
                                  </span>
                                  <div>
                                    <p className="font-bold text-[#302621]">{rc.nama_bahan}</p>
                                    <p className="text-[11px] text-[#7A695B] capitalize">{rc.kategori_bahan || 'Bahan Aroma'}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className="bg-[#8C6208]/10 text-[#8C6208] font-bold text-xs px-2.5 py-1 rounded-full border border-[#8C6208]/20">
                                    {pct}%
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Harga Total Jual Akhir */}
                    <div className="bg-gradient-to-br from-[#FFFDF9] to-[#F9F5EC] p-5 rounded-2xl border border-[#E6D5B8] flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold text-[#7A695B] uppercase tracking-wider block">
                          Harga Jual Akhir Produk
                        </span>
                        <p className="text-[11px] text-[#A39171]">
                          Ukuran {curSize?.size} ml • Termasuk botol, tutup, cairan, & jasa maklon
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-black text-[#8C6208]">
                          Rp {fmt(curSize?.finalPrice || 0)}
                        </span>
                        <p className="text-[10px] text-slate-500 font-semibold">/ pcs</p>
                      </div>
                    </div>

                  </>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-[#E6D5B8] bg-[#F9F5EC] flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setSelectedVariantDetail(null)}
                className="border-[#E6D5B8] text-[#7A695B] hover:bg-white text-xs font-bold rounded-xl"
              >
                Tutup
              </Button>

              <Button
                onClick={() => {
                  setSelectedVariantDetail(null);
                  navigate('/pesan-po');
                }}
                className="bg-[#302621] hover:bg-[#1A1411] text-white font-bold text-xs px-6 h-10 rounded-xl shadow-md gap-2"
              >
                <ShoppingBag className="w-4 h-4" />
                <span>Pesan Repeat Order Sekarang</span>
              </Button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
